import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { feedbackService } from '../services/feedbackService';
import apiClient from '../services/apiClient';
import { useAuth } from '../auth/AuthContext';
import PageHeader from '../components/ui/PageHeader';
import '../style/pages/FeedbackPage.css';
import { coincideBusqueda } from '../utils/textoComparable';

const ESTADOS = [
    'PENDIENTE',
    'EN_REVISION',
    'EN_PROCESO',
    'ESPERANDO_RESPUESTA',
    'SOLUCIONADO',
    'CANCELADO',
];

const ESTADO_LABELS = {
    PENDIENTE: 'Pendiente',
    EN_REVISION: 'En revisión',
    EN_PROCESO: 'En proceso',
    ESPERANDO_RESPUESTA: 'Esperando respuesta',
    SOLUCIONADO: 'Solucionado',
    CANCELADO: 'Cancelado',
};

const ESTADO_STYLES = {
    PENDIENTE: { background: '#fef3c7', color: '#92400e' },
    EN_REVISION: { background: '#dbeafe', color: '#1d4ed8' },
    EN_PROCESO: { background: '#ede9fe', color: '#6d28d9' },
    ESPERANDO_RESPUESTA: { background: '#e0f2fe', color: '#0369a1' },
    SOLUCIONADO: { background: '#dcfce7', color: '#15803d' },
    CANCELADO: { background: '#fee2e2', color: '#b91c1c' },
};

const ESTADOS_INTERACCION_USUARIO = ['PENDIENTE', 'ESPERANDO_RESPUESTA'];

const ETIQUETA_LABELS = {
    BUG: 'Error de sistema',
    BACKEND: 'Error al guardar',
    UI: 'Problema visual',
    SUGERENCIA: 'Sugerencia',
    'PRIORIDAD ALTA': 'Urgente',
};

const formatDateTime = (value) => {
    if (!value) return 'Sin fecha';
    return new Intl.DateTimeFormat('es-AR', {
        dateStyle: 'short',
        timeStyle: 'short',
    }).format(new Date(value));
};

const estadoLabel = (estado) => ESTADO_LABELS[estado] || estado || 'Sin estado';
const etiquetaLabel = (nombre) => ETIQUETA_LABELS[(nombre || '').trim().toUpperCase()] || nombre;
const esAdjuntoImagen = (url = '') => /\.(png|jpe?g|gif|webp|bmp|svg)(\?.*)?$/i.test(url);

const extraerImagenDesdePortapapeles = (event, prefijo) => {
    const items = event.clipboardData?.items || [];
    for (const item of items) {
        if (!item.type || !item.type.startsWith('image/')) {
            continue;
        }

        const blob = item.getAsFile();
        if (!blob) {
            continue;
        }

        const extension = (item.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
        const nombre = `${prefijo}-${Date.now()}.${extension}`;
        return new File([blob], nombre, { type: item.type });
    }

    return null;
};

const normalizarEtiqueta = (value) => value.trim().replace(/\s+/g, ' ');

const obtenerMensajeError = (error, fallback) => {
    const data = error?.response?.data;

    if (typeof data === 'string') {
        return data;
    }

    if (data && typeof data === 'object') {
        return data.message || data.error || fallback;
    }

    return error?.message || fallback;
};

const FeedbackPage = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { ticketId } = useParams();
    const rolUsuario = (user?.rol || '').toUpperCase();
    const esVistaProgramador = rolUsuario === 'ADMIN' || rolUsuario === 'PROGRAMADOR';
    const esVistaDetalleProgramador = esVistaProgramador && !!ticketId;
    const [tickets, setTickets] = useState([]);
    const [etiquetas, setEtiquetas] = useState([]);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [error, setError] = useState('');
    const [query, setQuery] = useState('');
    const [estadoFiltro, setEstadoFiltro] = useState('TODOS');
    const [modalNuevoTicket, setModalNuevoTicket] = useState(false);
    const [modalSeguimientoOpen, setModalSeguimientoOpen] = useState(false);
    const [previewAdjunto, setPreviewAdjunto] = useState({ url: '', esImagen: false });
    const [adjuntoBlobUrls, setAdjuntoBlobUrls] = useState({});
    const [creating, setCreating] = useState(false);
    const [submittingActivity, setSubmittingActivity] = useState(false);
    const [takingTicket, setTakingTicket] = useState(false);
    const [rejectingTicket, setRejectingTicket] = useState(false);
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const etiquetaComboboxRef = useRef(null);
    const [etiquetaDropdownAbierto, setEtiquetaDropdownAbierto] = useState(false);
    const [ticketForm, setTicketForm] = useState({
        titulo: '',
        descripcionInicial: '',
        etiquetaIds: [],
        nuevasEtiquetas: [],
        etiquetaQuery: '',
    });
    const [ticketFile, setTicketFile] = useState(null);
    const [activityForm, setActivityForm] = useState({ comentario: '' });
    const [activityFile, setActivityFile] = useState(null);

    const ticketStats = useMemo(() => {
        const total = tickets.length;
        const abiertos = tickets.filter((ticket) => !['SOLUCIONADO', 'CANCELADO'].includes(ticket.estado)).length;
        const cerrados = total - abiertos;
        return { total, abiertos, cerrados };
    }, [tickets]);

    const visibleTickets = useMemo(() => {
        return tickets.filter((ticket) => {
            const coincideEstado = estadoFiltro === 'TODOS' || ticket.estado === estadoFiltro;
            const coincideTexto = coincideBusqueda(
                [ticket.titulo, ticket.estado, ticket.usuarioNombre, ticket.usuarioId?.toString(), ticket.desarrolladorId?.toString()],
                query);
            return coincideEstado && coincideTexto;
        });
    }, [tickets, query, estadoFiltro]);

    const cargarDatos = async () => {
        try {
            setLoading(true);
            const [ticketsResponse, etiquetasResponse] = await Promise.all([
                feedbackService.listarTickets(),
                feedbackService.listarEtiquetas(),
            ]);
            setTickets(ticketsResponse.data || []);
            setEtiquetas(etiquetasResponse.data || []);
            setError('');
            if (!ticketsResponse.data?.some((ticket) => ticket.id === selectedTicket?.id)) {
                setSelectedTicket(null);
            }
        } catch (err) {
            setError(obtenerMensajeError(err, 'No se pudo cargar el módulo de feedback'));
        } finally {
            setLoading(false);
        }
    };

    const cargarDetalle = async (ticketId) => {
        if (!ticketId) return;
        try {
            setLoadingDetail(true);
            const response = await feedbackService.obtenerTicket(ticketId);
            setSelectedTicket(response.data);
            setTickets((prev) => prev.map((ticket) => (ticket.id === response.data.id ? response.data : ticket)));
        } catch (err) {
            setError(obtenerMensajeError(err, 'No se pudo cargar el detalle del ticket'));
        } finally {
            setLoadingDetail(false);
        }
    };

    useEffect(() => {
        cargarDatos();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (esVistaProgramador && ticketId) {
            cargarDetalle(ticketId);
            return;
        }

        if (esVistaProgramador && selectedTicket?.id) {
            cargarDetalle(selectedTicket.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ticketId, selectedTicket?.id, esVistaProgramador]);

    useEffect(() => {
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                if (previewAdjunto.url) {
                    setPreviewAdjunto({ url: '', esImagen: false });
                    return;
                }

                if (modalSeguimientoOpen) {
                    setModalSeguimientoOpen(false);
                }
            }
        };

        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [modalSeguimientoOpen, previewAdjunto.url]);

    useEffect(() => {
        return () => {
            Object.values(adjuntoBlobUrls).forEach((entry) => {
                if (entry?.blobUrl) {
                    URL.revokeObjectURL(entry.blobUrl);
                }
            });
        };
    }, [adjuntoBlobUrls]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (etiquetaComboboxRef.current && !etiquetaComboboxRef.current.contains(event.target)) {
                setTicketForm((prev) => ({ ...prev, etiquetaQuery: '' }));
                setEtiquetaDropdownAbierto(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const agregarEtiquetaExistente = (id) => {
        setTicketForm((prev) => {
            if (prev.etiquetaIds.includes(id)) {
                return { ...prev, etiquetaQuery: '' };
            }

            return {
                ...prev,
                etiquetaIds: [...prev.etiquetaIds, id],
                etiquetaQuery: '',
            };
        });
        setEtiquetaDropdownAbierto(false);
    };

    const quitarEtiquetaExistente = (id) => {
        setTicketForm((prev) => ({
            ...prev,
            etiquetaIds: prev.etiquetaIds.filter((item) => item !== id),
        }));
    };

    const agregarEtiquetaNueva = (valor) => {
        const nombre = normalizarEtiqueta(valor);
        if (!nombre) return;

        setTicketForm((prev) => {
            const coincideExistente = etiquetas.some(
                (etiqueta) => etiqueta.nombre?.trim().toLowerCase() === nombre.toLowerCase(),
            );
            const yaAgregada = prev.nuevasEtiquetas.some(
                (etiqueta) => etiqueta.toLowerCase() === nombre.toLowerCase(),
            );

            if (coincideExistente || yaAgregada) {
                return { ...prev, etiquetaQuery: '' };
            }

            return {
                ...prev,
                nuevasEtiquetas: [...prev.nuevasEtiquetas, nombre],
                etiquetaQuery: '',
            };
        });
        setEtiquetaDropdownAbierto(false);
    };

    const quitarEtiquetaNueva = (nombre) => {
        setTicketForm((prev) => ({
            ...prev,
            nuevasEtiquetas: prev.nuevasEtiquetas.filter((etiqueta) => etiqueta !== nombre),
        }));
    };

    const etiquetaQueryNormalizada = normalizarEtiqueta(ticketForm.etiquetaQuery);
    const etiquetaQueryLower = etiquetaQueryNormalizada.toLowerCase();
    const etiquetasSeleccionadas = useMemo(() => {
        const existentes = etiquetas.filter((etiqueta) => ticketForm.etiquetaIds.includes(etiqueta.id));
        const nuevas = ticketForm.nuevasEtiquetas.map((nombre) => ({ nombre, tipo: 'nueva' }));
        return [
            ...existentes.map((etiqueta) => ({ id: etiqueta.id, nombre: etiqueta.nombre, tipo: 'existente' })),
            ...nuevas,
        ];
    }, [etiquetas, ticketForm.etiquetaIds, ticketForm.nuevasEtiquetas]);

    const sugerenciasEtiquetas = useMemo(() => {
        if (!etiquetaQueryNormalizada) {
            return etiquetas
                .filter((etiqueta) => !ticketForm.etiquetaIds.includes(etiqueta.id))
                .slice(0, 6);
        }

        return etiquetas
            .filter((etiqueta) => !ticketForm.etiquetaIds.includes(etiqueta.id))
            .filter((etiqueta) => etiqueta.nombre?.toLowerCase().includes(etiquetaQueryLower))
            .slice(0, 6);
    }, [etiquetas, ticketForm.etiquetaIds, etiquetaQueryNormalizada, etiquetaQueryLower]);

    const yaExisteEtiquetaExacta = etiquetas.some(
        (etiqueta) => etiqueta.nombre?.trim().toLowerCase() === etiquetaQueryLower,
    );

    const handleEtiquetaKeyDown = (event) => {
        if (event.key === 'Enter' || event.key === ',') {
            event.preventDefault();
            if (sugerenciasEtiquetas.length > 0) {
                agregarEtiquetaExistente(sugerenciasEtiquetas[0].id);
                return;
            }

            if (etiquetaQueryNormalizada && !yaExisteEtiquetaExacta) {
                agregarEtiquetaNueva(etiquetaQueryNormalizada);
            }
        }

        if (event.key === 'Backspace' && !ticketForm.etiquetaQuery && ticketForm.nuevasEtiquetas.length > 0) {
            quitarEtiquetaNueva(ticketForm.nuevasEtiquetas[ticketForm.nuevasEtiquetas.length - 1]);
        }
    };

    const handleCrearTicket = async (event) => {
        event.preventDefault();
        try {
            setCreating(true);
            const payload = {
                usuarioId: user?.id,
                titulo: ticketForm.titulo.trim(),
                descripcionInicial: ticketForm.descripcionInicial.trim(),
                etiquetaIds: ticketForm.etiquetaIds,
                nuevasEtiquetas: ticketForm.nuevasEtiquetas,
            };
            const response = await feedbackService.crearTicket(payload, ticketFile);
            await cargarDatos();
            setSelectedTicket(response.data);
            setModalNuevoTicket(false);
            setTicketForm({ titulo: '', descripcionInicial: '', etiquetaIds: [], nuevasEtiquetas: [], etiquetaQuery: '' });
            setTicketFile(null);
        } catch (err) {
            setError(obtenerMensajeError(err, 'No se pudo crear el ticket'));
        } finally {
            setCreating(false);
        }
    };

    const handleAgregarActividad = async (event) => {
        event.preventDefault();
        if (!selectedTicket?.id) return;
        try {
            setSubmittingActivity(true);
            const payload = {
                remitenteId: user?.id || null,
                comentario: activityForm.comentario.trim(),
            };
            const response = await feedbackService.agregarActividad(selectedTicket.id, payload, activityFile);
            setSelectedTicket(response.data);
            setTickets((prev) => prev.map((ticket) => (ticket.id === response.data.id ? response.data : ticket)));
            setActivityForm({ comentario: '' });
            setActivityFile(null);
        } catch (err) {
            setError(obtenerMensajeError(err, 'No se pudo agregar la actividad'));
        } finally {
            setSubmittingActivity(false);
        }
    };

    const handleCambiarEstado = async (estado) => {
        if (!selectedTicket?.id || estado === selectedTicket.estado) return;
        try {
            const response = await feedbackService.cambiarEstado(selectedTicket.id, estado);
            setSelectedTicket(response.data);
            setTickets((prev) => prev.map((ticket) => (ticket.id === response.data.id ? response.data : ticket)));
        } catch (err) {
            setError(obtenerMensajeError(err, 'No se pudo cambiar el estado'));
        }
    };

    const handleTomarTrabajo = async () => {
        if (!selectedTicketFull?.id) return;
        try {
            setTakingTicket(true);
            const response = await feedbackService.tomarTicket(selectedTicketFull.id);
            setSelectedTicket(response.data);
            setTickets((prev) => prev.map((ticket) => (ticket.id === response.data.id ? response.data : ticket)));
            setShowRejectForm(false);
            setRejectReason('');
        } catch (err) {
            setError(obtenerMensajeError(err, 'No se pudo tomar el ticket'));
        } finally {
            setTakingTicket(false);
        }
    };

    const handleConfirmarRechazo = async () => {
        if (!selectedTicketFull?.id) return;
        if (!rejectReason.trim()) {
            setError('Debes indicar el motivo del rechazo.');
            return;
        }

        try {
            setRejectingTicket(true);
            const response = await feedbackService.rechazarTicket(selectedTicketFull.id, rejectReason.trim());
            setSelectedTicket(response.data);
            setTickets((prev) => prev.map((ticket) => (ticket.id === response.data.id ? response.data : ticket)));
            setShowRejectForm(false);
            setRejectReason('');
        } catch (err) {
            setError(obtenerMensajeError(err, 'No se pudo rechazar el ticket'));
        } finally {
            setRejectingTicket(false);
        }
    };

    const handlePasteTicketAdjunto = (event) => {
        const imagen = extraerImagenDesdePortapapeles(event, 'ticket');
        if (!imagen) return;
        event.preventDefault();
        setTicketFile(imagen);
    };

    const handlePasteActividadAdjunto = (event) => {
        const imagen = extraerImagenDesdePortapapeles(event, 'actividad');
        if (!imagen) return;
        event.preventDefault();
        setActivityFile(imagen);
    };

    const selectedTicketFull = selectedTicket
        ? tickets.find((ticket) => ticket.id === selectedTicket.id) || selectedTicket
        : null;
    const gridClassName = esVistaDetalleProgramador
        ? 'feedback-grid feedback-grid-detail'
        : 'feedback-grid feedback-grid-list';

    const desarrolladorActualId = Number(selectedTicketFull?.desarrolladorId || 0);
    const usuarioActualId = Number(user?.id || 0);
    const asignadoAMi = desarrolladorActualId > 0 && desarrolladorActualId === usuarioActualId;
    const asignadoAOtro = desarrolladorActualId > 0 && desarrolladorActualId !== usuarioActualId;
    const ticketFinalizado = ['SOLUCIONADO', 'CANCELADO'].includes(selectedTicketFull?.estado);
    const puedeTomar = !!selectedTicketFull && !ticketFinalizado && (!asignadoAOtro || rolUsuario === 'ADMIN');
    const puedeRechazar = !!selectedTicketFull && !ticketFinalizado && (asignadoAMi || rolUsuario === 'ADMIN');

    const abrirModalSeguimiento = async (ticket) => {
        setSelectedTicket(ticket);
        setModalSeguimientoOpen(true);
        await cargarDetalle(ticket.id);
    };

    const cerrarModalSeguimiento = () => {
        setModalSeguimientoOpen(false);
        setActivityForm({ comentario: '' });
        setActivityFile(null);
        setPreviewAdjunto({ url: '', esImagen: false });
    };

    const normalizarRutaAdjunto = (urlAdjunto) => {
        if (!urlAdjunto) return null;

        if (urlAdjunto.startsWith('/api/')) {
            return urlAdjunto.replace('/api', '');
        }

        if (urlAdjunto.startsWith('/')) {
            return urlAdjunto;
        }

        return `/${urlAdjunto}`;
    };

    const obtenerAdjuntoAutenticado = async (urlAdjunto) => {
        if (!urlAdjunto) return null;
        if (adjuntoBlobUrls[urlAdjunto]) return adjuntoBlobUrls[urlAdjunto];

        const ruta = normalizarRutaAdjunto(urlAdjunto);
        const response = await apiClient.get(ruta, { responseType: 'blob' });
        const blobUrl = URL.createObjectURL(response.data);
        const entry = { blobUrl, mimeType: response.data?.type || '' };

        setAdjuntoBlobUrls((prev) => {
            if (prev[urlAdjunto]) {
                URL.revokeObjectURL(blobUrl);
                return prev;
            }
            return { ...prev, [urlAdjunto]: entry };
        });

        return entry;
    };

    useEffect(() => {
        const actividades = selectedTicketFull?.historialActividad || [];
        const urlsImagenes = actividades
            .map((actividad) => actividad?.urlAdjunto)
            .filter((url) => !!url && esAdjuntoImagen(url));

        if (urlsImagenes.length === 0) {
            return;
        }

        urlsImagenes.forEach((url) => {
            if (!adjuntoBlobUrls[url]) {
                obtenerAdjuntoAutenticado(url).catch(() => {
                    setAdjuntoBlobUrls((prev) => (prev[url] ? prev : { ...prev, [url]: { error: true } }));
                });
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedTicketFull?.id]);

    const handleVerAdjunto = async (urlAdjunto) => {
        try {
            const adjunto = await obtenerAdjuntoAutenticado(urlAdjunto);
            if (adjunto?.blobUrl) {
                const esImagenMime = adjunto.mimeType?.startsWith('image/');
                setPreviewAdjunto({
                    url: adjunto.blobUrl,
                    esImagen: esImagenMime || esAdjuntoImagen(urlAdjunto),
                });
            } else {
                setError('No se pudo abrir el adjunto');
            }
        } catch {
            setError('No se pudo abrir el adjunto');
        }
    };

    const handleVerImagenAdjunta = async (urlAdjunto) => {
        await handleVerAdjunto(urlAdjunto);
    };

    const getMiniaturaAdjunto = (urlAdjunto) => adjuntoBlobUrls[urlAdjunto]?.blobUrl || '';
    const adjuntoConError = (urlAdjunto) => !!adjuntoBlobUrls[urlAdjunto]?.error;

    return (
        <div className="feedback-shell area-herramientas">
            <PageHeader
                variante="banner"
                icono="chat-square-text-fill"
                titulo={esVistaProgramador ? 'Tickets de errores y sugerencias' : 'Mis tickets de soporte'}
                descripcion={
                    esVistaProgramador
                        ? 'Canal interno para reportar fallas, coordinar seguimiento y dejar trazabilidad de cada intercambio. Los adjuntos se comprimen y la limpieza automática mantiene el almacenamiento bajo control.'
                        : 'Desde acá podés crear tickets y seguir su estado hasta que queden finalizados o solucionados.'
                }
            />

            {error && (
                <div className="alert alert-danger mt-3 mb-0">{error}</div>
            )}

            <div className="feedback-stats mt-3">
                <div className="feedback-stat">
                    <div className="feedback-stat-label">Tickets</div>
                    <div className="feedback-stat-value">{ticketStats.total}</div>
                </div>
                <div className="feedback-stat">
                    <div className="feedback-stat-label">Abiertos</div>
                    <div className="feedback-stat-value">{ticketStats.abiertos}</div>
                </div>
                <div className="feedback-stat">
                    <div className="feedback-stat-label">Cerrados</div>
                    <div className="feedback-stat-value">{ticketStats.cerrados}</div>
                </div>
            </div>

            <div className="feedback-toolbar mt-3">
                <button className="feedback-primary-btn" onClick={() => setModalNuevoTicket(true)}>
                    <span className="feedback-primary-btn-icon">+</span>
                    <span>Nuevo ticket</span>
                </button>
                <input
                    type="text"
                    className="form-control"
                    style={{ maxWidth: 360 }}
                    placeholder={esVistaProgramador ? 'Buscar por título, estado o usuario' : 'Buscar por título o estado'}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
                <div className="feedback-chip-list">
                    <button className={`feedback-chip ${estadoFiltro === 'TODOS' ? 'active' : ''}`} onClick={() => setEstadoFiltro('TODOS')}>
                        Todos
                    </button>
                    {ESTADOS.map((estado) => (
                        <button
                            key={estado}
                            className={`feedback-chip ${estadoFiltro === estado ? 'active' : ''}`}
                            onClick={() => setEstadoFiltro(estado)}
                        >
                            {estadoLabel(estado)}
                        </button>
                    ))}
                </div>
            </div>

            <div className={gridClassName}>
                {!esVistaDetalleProgramador && (
                <section className="feedback-card">
                    <div className="feedback-card-header">
                        <div>
                            <h2 className="feedback-card-title">{esVistaProgramador ? 'Tickets' : 'Mis tickets'}</h2>
                            {!esVistaProgramador && (
                                <p className="feedback-card-subtitle">Seguimiento de estado y fecha de resolución</p>
                            )}
                        </div>
                        {loading && <span className="feedback-mini">Cargando...</span>}
                    </div>

                    <div className="feedback-list">
                        {!loading && visibleTickets.length === 0 && (
                            <div className="feedback-empty-state feedback-empty-state-rich">
                                <div className="feedback-empty-icon" aria-hidden="true">🗂️</div>
                                <div className="feedback-empty-title">Sin resultados para estos filtros</div>
                                <div className="feedback-empty-copy">Probá con otro estado o limpiá la búsqueda para volver a ver tickets.</div>
                            </div>
                        )}

                        {visibleTickets.map((ticket) => {
                            const active = selectedTicketFull?.id === ticket.id;
                            const estadoFinal = ticket.estado === 'SOLUCIONADO' || ticket.estado === 'CANCELADO';
                            const fechaFinalizacion = estadoFinal ? formatDateTime(ticket.fechaActualizacion) : 'Pendiente';

                            if (!esVistaProgramador) {
                                return (
                                    <button
                                        key={ticket.id}
                                        className="feedback-ticket-item mb-3"
                                        onClick={() => abrirModalSeguimiento(ticket)}
                                    >
                                        <div className={`feedback-ticket-card ${active ? 'active' : ''}`}>
                                            <div className="feedback-ticket-top">
                                                <div>
                                                    <h3 className="feedback-ticket-title">{ticket.titulo}</h3>
                                                    <p className="feedback-ticket-meta">
                                                        Creado {formatDateTime(ticket.fechaCreacion)}
                                                    </p>
                                                </div>
                                                <span className="feedback-pill status" style={ESTADO_STYLES[ticket.estado]}>
                                                    {estadoLabel(ticket.estado)}
                                                </span>
                                            </div>
                                            <div className="feedback-ticket-meta mt-2">
                                                Finalizado / solucionado: <strong>{fechaFinalizacion}</strong>
                                            </div>
                                            <div className="feedback-badge-row">
                                                {(ticket.etiquetas || []).map((etiqueta) => (
                                                    <span key={etiqueta.id} className="feedback-pill tag">
                                                        {etiquetaLabel(etiqueta.nombre)}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    </button>
                                );
                            }

                            return (
                                <button
                                    key={ticket.id}
                                    className="feedback-ticket-item mb-3"
                                    onClick={() => {
                                        setSelectedTicket(ticket);
                                        navigate(`/herramientas/feedback/${ticket.id}`);
                                    }}
                                >
                                    <div className={`feedback-ticket-card ${active ? 'active' : ''}`}>
                                        <div className="feedback-ticket-top">
                                            <div>
                                                <h3 className="feedback-ticket-title">{ticket.titulo}</h3>
                                                <p className="feedback-ticket-meta">
                                                    {ticket.usuarioNombre || `Usuario #${ticket.usuarioId}`} · Actualizado {formatDateTime(ticket.fechaActualizacion)}
                                                </p>
                                                {ticket.desarrolladorNombre && (
                                                    <p className="feedback-ticket-meta">Asignado a {ticket.desarrolladorNombre}</p>
                                                )}
                                            </div>
                                            <span className="feedback-pill status" style={ESTADO_STYLES[ticket.estado]}>
                                                {estadoLabel(ticket.estado)}
                                            </span>
                                        </div>

                                        <div className="feedback-badge-row">
                                            {(ticket.etiquetas || []).slice(0, 4).map((etiqueta) => (
                                                <span key={etiqueta.id} className="feedback-pill tag">
                                                    {etiquetaLabel(etiqueta.nombre)}
                                                </span>
                                            ))}
                                            {(ticket.etiquetas || []).length > 4 && (
                                                <span className="feedback-pill tag">+{ticket.etiquetas.length - 4}</span>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </section>
                )}

                {esVistaDetalleProgramador && (
                <section className="feedback-card">
                    <div className="feedback-card-header">
                        <div>
                            <h2 className="feedback-card-title">Detalle</h2>
                            <p className="feedback-card-subtitle">Historial, estado y seguimiento</p>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            {loadingDetail && <span className="feedback-mini">Actualizando...</span>}
                            <button
                                type="button"
                                className="feedback-btn-secondary feedback-btn-secondary-sm"
                                onClick={() => navigate('/herramientas/feedback')}
                            >
                                Volver a tickets
                            </button>
                        </div>
                    </div>

                    {!selectedTicketFull ? (
                        <div className="feedback-empty-state feedback-empty-state-rich">
                            <div className="feedback-empty-icon" aria-hidden="true">🧭</div>
                            <div className="feedback-empty-title">Todavía no hay un ticket seleccionado</div>
                            <div className="feedback-empty-copy">Elegí un ticket de la lista para abrir el detalle, su historial y sus acciones.</div>
                        </div>
                    ) : (
                        <div className="feedback-detail p-3">
                            <div className="feedback-detail-grid">
                                <div className="feedback-detail-main">
                                    <div className="feedback-detail-head">
                                        <div>
                                            <h3 className="feedback-detail-title">{selectedTicketFull.titulo}</h3>
                                            <div className="feedback-detail-text">
                                                Creado por {selectedTicketFull.usuarioNombre || `Usuario #${selectedTicketFull.usuarioId}`} · Última actualización {formatDateTime(selectedTicketFull.fechaActualizacion)}
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <h4 className="feedback-card-title mb-3">Historial de actividad</h4>
                                        <div className="feedback-timeline feedback-timeline-detail">
                                            {(selectedTicketFull.historialActividad || []).map((actividad) => (
                                                <div key={actividad.id} className="feedback-event">
                                                    <div className="feedback-event-dot" />
                                                    <div className="feedback-event-card">
                                                        <div className="feedback-event-top">
                                                            <div className="feedback-event-author">
                                                                {actividad.remitenteId == null ? 'Sistema' : (actividad.remitenteNombre || `Remitente #${actividad.remitenteId}`)}
                                                            </div>
                                                            <div className="feedback-event-date">{formatDateTime(actividad.fechaEnvio)}</div>
                                                        </div>
                                                        <p className="feedback-event-comment">{actividad.comentario}</p>
                                                        {actividad.urlAdjunto && esAdjuntoImagen(actividad.urlAdjunto) && (
                                                            <button
                                                                type="button"
                                                                className="feedback-thumb-inline"
                                                                onClick={() => handleVerImagenAdjunta(actividad.urlAdjunto)}
                                                                title="Abrir imagen"
                                                            >
                                                                {getMiniaturaAdjunto(actividad.urlAdjunto) ? (
                                                                    <img src={getMiniaturaAdjunto(actividad.urlAdjunto)} alt="Miniatura adjunta" />
                                                                ) : (
                                                                    <span>{adjuntoConError(actividad.urlAdjunto) ? 'Imagen no disponible' : 'Cargando imagen...'}</span>
                                                                )}
                                                            </button>
                                                        )}
                                                        {actividad.urlAdjunto && !esAdjuntoImagen(actividad.urlAdjunto) && (
                                                            <a
                                                                href="#!"
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    handleVerAdjunto(actividad.urlAdjunto);
                                                                }}
                                                            >
                                                                Ver adjunto
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <form onSubmit={handleAgregarActividad} onPaste={handlePasteActividadAdjunto} className="card border-0 shadow-sm mt-2" style={{ borderRadius: 18 }}>
                                        <div className="card-body">
                                            <h4 className="feedback-card-title mb-3">Agregar actividad</h4>
                                            <div className="feedback-form-grid">
                                                <div className="feedback-form-full">
                                                    <label className="form-label">Comentario</label>
                                                    <textarea
                                                        className="form-control"
                                                        rows={4}
                                                        value={activityForm.comentario}
                                                        onChange={(e) => setActivityForm({ comentario: e.target.value })}
                                                        required
                                                    />
                                                </div>
                                                <div>
                                                    <label className="form-label">Adjunto opcional</label>
                                                    <input
                                                        type="file"
                                                        className="form-control"
                                                        onChange={(e) => setActivityFile(e.target.files?.[0] || null)}
                                                    />
                                                    <div className="feedback-mini mt-2">Tip: pegá una imagen con Ctrl+V.</div>
                                                    {activityFile && <div className="feedback-mini mt-1">Adjunto: {activityFile.name}</div>}
                                                </div>
                                            </div>
                                            <div className="d-flex justify-content-end gap-2 mt-3">
                                                <button
                                                    type="submit"
                                                    className="feedback-action-btn feedback-action-btn-primary"
                                                    disabled={submittingActivity}
                                                >
                                                    {submittingActivity ? 'Enviando...' : 'Publicar actividad'}
                                                </button>
                                            </div>
                                        </div>
                                    </form>
                                </div>

                                <aside className="feedback-detail-sidebar">
                                    <div className="feedback-sidebar-card feedback-sidebar-card-highlight">
                                        <label className="form-label">Estado del ticket</label>
                                        <select
                                            className="form-select feedback-select-prominent"
                                            value={selectedTicketFull.estado}
                                            onChange={(e) => handleCambiarEstado(e.target.value)}
                                        >
                                            {ESTADOS.map((estado) => (
                                                <option key={estado} value={estado}>{estadoLabel(estado)}</option>
                                            ))}
                                        </select>
                                        <div className="feedback-mini mt-2">El cambio se guarda automáticamente.</div>
                                    </div>

                                    <div className="feedback-sidebar-card">
                                        <div className="feedback-badge-row mt-0">
                                            <span className="feedback-pill status" style={ESTADO_STYLES[selectedTicketFull.estado]}>
                                                {estadoLabel(selectedTicketFull.estado)}
                                            </span>
                                            {(selectedTicketFull.etiquetas || []).map((etiqueta) => (
                                                <span key={etiqueta.id} className="feedback-pill tag feedback-pill-tag-strong">
                                                    {etiquetaLabel(etiqueta.nombre)}
                                                </span>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="feedback-stats feedback-stats-sidebar">
                                        <div className="feedback-stat">
                                            <div className="feedback-stat-label">Desarrollador</div>
                                            <div className="feedback-stat-value">{selectedTicketFull.desarrolladorNombre || 'Sin asignar'}</div>
                                        </div>
                                        <div className="feedback-stat">
                                            <div className="feedback-stat-label">Creado</div>
                                            <div className="feedback-stat-value">{formatDateTime(selectedTicketFull.fechaCreacion)}</div>
                                        </div>
                                        <div className="feedback-stat">
                                            <div className="feedback-stat-label">Actividad</div>
                                            <div className="feedback-stat-value">{selectedTicketFull.historialActividad?.length || 0}</div>
                                        </div>
                                    </div>

                                    <div className="feedback-dev-actions">
                                        <button
                                            type="button"
                                            className="feedback-action-btn feedback-action-btn-primary"
                                            disabled={!puedeTomar || takingTicket}
                                            onClick={handleTomarTrabajo}
                                        >
                                            {takingTicket ? 'Tomando...' : 'Tomar trabajo'}
                                        </button>
                                        <button
                                            type="button"
                                            className="feedback-action-btn feedback-action-btn-danger"
                                            disabled={!puedeRechazar || rejectingTicket}
                                            onClick={() => setShowRejectForm((prev) => !prev)}
                                        >
                                            Rechazar
                                        </button>
                                    </div>

                                    {asignadoAOtro && rolUsuario !== 'ADMIN' && (
                                        <div className="feedback-mini">Este ticket ya esta asignado a otro desarrollador.</div>
                                    )}

                                    {showRejectForm && (
                                        <div className="feedback-reject-box">
                                            <label className="form-label">Motivo del rechazo</label>
                                            <textarea
                                                className="form-control"
                                                rows={3}
                                                value={rejectReason}
                                                onChange={(e) => setRejectReason(e.target.value)}
                                                placeholder="Explica por que no puedes tomar este ticket"
                                            />
                                            <div className="d-flex justify-content-end gap-2 mt-2">
                                                <button
                                                    type="button"
                                                    className="feedback-btn-secondary"
                                                    onClick={() => {
                                                        setShowRejectForm(false);
                                                        setRejectReason('');
                                                    }}
                                                >
                                                    Cancelar
                                                </button>
                                                <button
                                                    type="button"
                                                    className="feedback-btn-danger-solid"
                                                    disabled={rejectingTicket}
                                                    onClick={handleConfirmarRechazo}
                                                >
                                                    {rejectingTicket ? 'Guardando...' : 'Confirmar rechazo'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </aside>
                            </div>
                        </div>
                    )}
                </section>
                )}
            </div>

            {!esVistaProgramador && modalSeguimientoOpen && (
                <div
                    className="feedback-modal-overlay"
                    role="presentation"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) {
                            cerrarModalSeguimiento();
                        }
                    }}
                >
                    <div className="feedback-modal-card" role="dialog" aria-modal="true" aria-label="Seguimiento del ticket">
                        <div className="feedback-modal-header">
                            <div>
                                <h3 className="feedback-modal-title">{selectedTicketFull?.titulo || 'Ticket'}</h3>
                                <div className="feedback-detail-text">Creado {formatDateTime(selectedTicketFull?.fechaCreacion)}</div>
                            </div>
                            <div className="feedback-modal-header-right">
                                <span className="feedback-pill status" style={ESTADO_STYLES[selectedTicketFull?.estado] || ESTADO_STYLES.PENDIENTE}>
                                    {estadoLabel(selectedTicketFull?.estado)}
                                </span>
                                <button className="feedback-modal-close" type="button" onClick={cerrarModalSeguimiento} aria-label="Cerrar seguimiento">
                                    ×
                                </button>
                            </div>
                        </div>

                        <div className="feedback-modal-body">
                            {loadingDetail ? (
                                <div className="feedback-empty-state">Cargando historial...</div>
                            ) : (
                                <div className="feedback-timeline feedback-timeline-modal">
                                    {(selectedTicketFull?.historialActividad || []).map((actividad) => {
                                        const esPropio = actividad.remitenteId === user?.id;
                                        return (
                                            <div key={actividad.id} className={`feedback-event ${esPropio ? 'own' : 'support'}`}>
                                                <div className="feedback-event-dot" />
                                                <div className="feedback-event-card">
                                                    <div className="feedback-event-top">
                                                        <div className="feedback-event-author">
                                                            {esPropio ? 'Vos' : 'Soporte técnico'}
                                                        </div>
                                                        <div className="feedback-event-date">{formatDateTime(actividad.fechaEnvio)}</div>
                                                    </div>
                                                    <p className="feedback-event-comment">{actividad.comentario}</p>
                                                    {actividad.urlAdjunto && esAdjuntoImagen(actividad.urlAdjunto) && (
                                                        <button
                                                            type="button"
                                                            className="feedback-thumb-inline"
                                                            onClick={() => handleVerImagenAdjunta(actividad.urlAdjunto)}
                                                            title="Abrir imagen"
                                                        >
                                                            {getMiniaturaAdjunto(actividad.urlAdjunto) ? (
                                                                <img src={getMiniaturaAdjunto(actividad.urlAdjunto)} alt="Miniatura adjunta" />
                                                            ) : (
                                                                <span>{adjuntoConError(actividad.urlAdjunto) ? 'Imagen no disponible' : 'Cargando imagen...'}</span>
                                                            )}
                                                        </button>
                                                    )}
                                                    {actividad.urlAdjunto && !esAdjuntoImagen(actividad.urlAdjunto) && (
                                                        <a
                                                            href="#!"
                                                            onClick={(e) => {
                                                                e.preventDefault();
                                                                handleVerAdjunto(actividad.urlAdjunto);
                                                            }}
                                                        >
                                                            Ver adjunto
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {selectedTicketFull && ESTADOS_INTERACCION_USUARIO.includes(selectedTicketFull.estado) && (
                            <form onSubmit={handleAgregarActividad} onPaste={handlePasteActividadAdjunto} className="feedback-modal-footer">
                                <textarea
                                    className="form-control"
                                    rows={2}
                                    placeholder="Escribí un mensaje para soporte"
                                    value={activityForm.comentario}
                                    onChange={(e) => setActivityForm({ comentario: e.target.value })}
                                    required
                                />
                                <div className="feedback-modal-actions">
                                    {activityFile && <span className="feedback-mini">{activityFile.name}</span>}
                                    <label className="feedback-clip-btn" title="Adjuntar imagen">
                                        Adj
                                        <input
                                            type="file"
                                            className="d-none"
                                            onChange={(e) => setActivityFile(e.target.files?.[0] || null)}
                                        />
                                    </label>
                                    <button type="submit" className="feedback-action-btn feedback-action-btn-primary" disabled={submittingActivity}>
                                        {submittingActivity ? 'Enviando...' : 'Enviar'}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {previewAdjunto.url && (
                <div className="feedback-image-preview" role="presentation" onMouseDown={() => setPreviewAdjunto({ url: '', esImagen: false })}>
                    <div className="feedback-adjunto-viewer" role="dialog" aria-modal="true" onMouseDown={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="feedback-modal-close feedback-adjunto-close"
                            onClick={() => setPreviewAdjunto({ url: '', esImagen: false })}
                            aria-label="Cerrar adjunto"
                        >
                            ×
                        </button>
                        {previewAdjunto.esImagen ? (
                            <img src={previewAdjunto.url} alt="Adjunto ampliado" className="feedback-image-preview-content" />
                        ) : (
                            <iframe src={previewAdjunto.url} title="Vista previa de adjunto" className="feedback-adjunto-frame" />
                        )}
                        <div className="feedback-adjunto-footer">
                            <a href={previewAdjunto.url} download className="btn btn-outline-light btn-sm">
                                Descargar
                            </a>
                        </div>
                    </div>
                </div>
            )}

            {modalNuevoTicket && (
                <div
                    className="feedback-modal-overlay feedback-create-overlay"
                    role="presentation"
                    onMouseDown={(e) => {
                        if (e.target === e.currentTarget) {
                            setModalNuevoTicket(false);
                        }
                    }}
                >
                    <div
                        className="feedback-modal-card feedback-create-card"
                        role="dialog"
                        aria-modal="true"
                        aria-label="Nuevo ticket"
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <div className="feedback-create-header">
                            <div className="feedback-create-title-wrap">
                                <span className="feedback-create-icon" aria-hidden="true">+</span>
                                <h5 className="feedback-create-title">Nuevo ticket</h5>
                            </div>
                            <button
                                type="button"
                                className="feedback-modal-close feedback-create-close"
                                onClick={() => setModalNuevoTicket(false)}
                                aria-label="Cerrar nuevo ticket"
                            >
                                ×
                            </button>
                        </div>
                        <form onSubmit={handleCrearTicket} onPaste={handlePasteTicketAdjunto} className="feedback-create-form">
                            <div className="feedback-create-body">
                                <div className="feedback-form-grid">
                                    <div className="feedback-form-full">
                                        <label className="form-label">Título</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={ticketForm.titulo}
                                            onChange={(e) => setTicketForm((prev) => ({ ...prev, titulo: e.target.value }))}
                                            required
                                        />
                                    </div>
                                    <div className="feedback-form-full">
                                        <label className="form-label">Descripción inicial</label>
                                        <textarea
                                            className="form-control"
                                            rows={4}
                                            value={ticketForm.descripcionInicial}
                                            onChange={(e) => setTicketForm((prev) => ({ ...prev, descripcionInicial: e.target.value }))}
                                            required
                                        />
                                    </div>
                                    <div className="feedback-form-full">
                                        <label className="form-label">Tipo</label>
                                        <div className="feedback-combobox" ref={etiquetaComboboxRef}>
                                            <div className="feedback-selected-tags">
                                                {etiquetasSeleccionadas.map((etiqueta) => (
                                                    <span key={`${etiqueta.tipo}-${etiqueta.id || etiqueta.nombre}`} className="feedback-selected-tag">
                                                        {etiquetaLabel(etiqueta.nombre)}
                                                        <button
                                                            type="button"
                                                            className="feedback-selected-tag-remove"
                                                            onClick={() => (etiqueta.tipo === 'existente' ? quitarEtiquetaExistente(etiqueta.id) : quitarEtiquetaNueva(etiqueta.nombre))}
                                                            aria-label={`Quitar etiqueta ${etiqueta.nombre}`}
                                                        >
                                                            ×
                                                        </button>
                                                    </span>
                                                ))}
                                                <input
                                                    type="text"
                                                    className="feedback-combobox-input"
                                                    placeholder="Escribí para buscar o crear un tipo"
                                                    value={ticketForm.etiquetaQuery}
                                                    onFocus={() => setEtiquetaDropdownAbierto(true)}
                                                    onChange={(e) => {
                                                        setTicketForm((prev) => ({ ...prev, etiquetaQuery: e.target.value }));
                                                        setEtiquetaDropdownAbierto(true);
                                                    }}
                                                    onKeyDown={handleEtiquetaKeyDown}
                                                />
                                            </div>
                                            {etiquetaDropdownAbierto && (
                                                <div className="feedback-combobox-dropdown">
                                                    {etiquetas.length === 0 && (
                                                        <div className="feedback-combobox-empty">No hay etiquetas disponibles.</div>
                                                    )}
                                                    {sugerenciasEtiquetas.map((etiqueta) => (
                                                        <button
                                                            key={etiqueta.id}
                                                            type="button"
                                                            className="feedback-combobox-option"
                                                            onMouseDown={(e) => {
                                                                e.preventDefault();
                                                                agregarEtiquetaExistente(etiqueta.id);
                                                                setEtiquetaDropdownAbierto(false);
                                                            }}
                                                        >
                                                            <span>{etiquetaLabel(etiqueta.nombre)}</span>
                                                            <small>Usar existente</small>
                                                        </button>
                                                    ))}
                                                    {etiquetaQueryNormalizada && !yaExisteEtiquetaExacta && (
                                                        <button
                                                            type="button"
                                                            className="feedback-combobox-option create"
                                                            onMouseDown={(e) => {
                                                                e.preventDefault();
                                                                agregarEtiquetaNueva(etiquetaQueryNormalizada);
                                                                setEtiquetaDropdownAbierto(false);
                                                            }}
                                                        >
                                                            <span>Crear “{etiquetaQueryNormalizada}”</span>
                                                            <small>Etiqueta nueva</small>
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="form-label">Adjunto opcional</label>
                                        <input
                                            type="file"
                                            className="form-control"
                                            onChange={(e) => setTicketFile(e.target.files?.[0] || null)}
                                        />
                                        <div className="feedback-mini mt-2">Tip: pegá una imagen con Ctrl+V.</div>
                                        {ticketFile && <div className="feedback-mini mt-1">Adjunto: {ticketFile.name}</div>}
                                    </div>
                                </div>
                            </div>
                            <div className="feedback-create-footer">
                                <button type="button" className="feedback-btn-secondary" onClick={() => setModalNuevoTicket(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="feedback-action-btn feedback-action-btn-primary" disabled={creating}>
                                    {creating ? 'Creando...' : 'Crear ticket'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeedbackPage;